import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import audioService from '../services/AudioService';
import {SET_SCAN_PACK_CURRENT_ORDER} from '../constants';
import {
  setBoxChampState,
  setScanPackBarcodeInput,
  setScanPackMainInputFocus,
  setActiveBoxIndex,
  setCurrentItem,
  getStateValues
} from './scanpackScanningSetters';
import {v2FocusBlockingModalOpen} from '../components/scanpack/boxChampModalFlags';
import {saveAndSubmitPlanOpLog} from './submitLogThrottle';
import {
  BOX_CHAMP_MANUAL_NAV_MESSAGE,
  BOX_CHAMP_SCAN_ALL_MESSAGE,
  clearBoxChampConfirmationTimer,
  showBoxChampConfirmation,
  showSetAsideMessage,
  setItemAside
} from './boxChampGuidedNotices';
import {getFirstItem} from '../components/ScanpackScanning/utils/scanning/itemTreeMutators';
import {CalculateBoxChampBoxes} from './orderActions';
import {UpdateProductInfo} from './productAction';
import {firstVerifyEvent, handoffEvent} from '../components/scanpack/boxChampEvents';
import {planOpEvent} from '../components/scanpack/boxChampOptions';
import {
  classifyPausedInput,
  HANDOFF_IDLE,
  HANDOFF_PAUSED,
  HANDOFF_AWAITING_FIRST_VERIFY
} from '../components/scanpack/boxChampHandoff';
import {substitutionStep, SUBSTITUTION_PENDING} from '../components/scanpack/boxChampSubstitution';
import {matchScannedBox} from '../components/scanpack/boxChampVerify';
import {
  getGuidedView,
  getGuidedHandoff,
  isGuidedOrderPacked,
  getGuidedUnboxedBarcodes,
  guidedLabelFor,
  guidedHandoffKey,
  guidedHandoffOwnsFocus,
  boxChampOf,
  scanPackSettingsOf
} from '../components/ScanpackScanning/utils/boxChampGuidedSelectors';

export const BOX_CHAMP_CATALOG_ERROR =
  'Box catalog unavailable — box substitution and the box list may not work. Tap to retry.';
const MAX_CATALOG_FAILURES = 3;

const userNameOf = state => {
  const user = state.user?.userInfo;
  return user?.username || user?.name || '';
};

let catalogRequested = false;
let catalogFailures = 0;
let catalogVisit = 0;
let catalogSeq = 0;

const clearCatalogAttempts = () => {
  catalogRequested = false;
  catalogFailures = 0;
};

export const resetBoxChampCatalogRequest = () => {
  clearCatalogAttempts();
  catalogVisit += 1;
};

export function loadBoxChampCatalog() {
  return async (dispatch, getState) => {
    if (catalogRequested) return;
    catalogRequested = true;
    catalogSeq += 1;
    const seq = catalogSeq;
    const visit = catalogVisit;
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const res = await axios({
        method: 'get',
        url: `${url}/box_types/packing_index.json`,
        headers: {Authorization: `Bearer ${access_token}`}
      });
      if (visit !== catalogVisit) return;
      if (!Array.isArray(res?.data?.box_types)) throw new Error('No box types in the catalog response');
      if (seq !== catalogSeq && boxChampOf(getState()).catalog?.length) return;
      catalogFailures = 0;
      dispatch(setBoxChampState({catalog: res.data.box_types, catalogError: ''}));
    } catch (e) {
      if (visit !== catalogVisit || seq !== catalogSeq) return;
      if (boxChampOf(getState()).catalog?.length) return;
      catalogFailures += 1;
      if (catalogFailures < MAX_CATALOG_FAILURES) catalogRequested = false;
      dispatch(setBoxChampState({catalogError: BOX_CHAMP_CATALOG_ERROR}));
    }
  };
}

export function retryBoxChampCatalog() {
  return dispatch => {
    clearCatalogAttempts();
    dispatch(setBoxChampState({catalogError: ''}));
    return dispatch(loadBoxChampCatalog());
  };
}

const playGuidedSuccess = () => {
  audioService.playSuccessSound();
};

export const boxChampConfirmMessage = handoff => {
  if (handoff.kind === HANDOFF_AWAITING_FIRST_VERIFY) {
    return `${handoff.expectedBox?.name || 'Box'} confirmed — go ahead and start packing.`;
  }
  const sealedName = handoff.sealBox?.name || 'This box';
  return handoff.nextBox
    ? `${sealedName} confirmed — now packing ${handoff.nextBox.name}.`
    : `${sealedName} confirmed.`;
};

export const boxChampRejectionText = (verdict, handoff) => {
  if (verdict.reason === 'wrong_box') {
    return `That isn't the right box — scan ${verdict.expectedName}.`;
  }
  if (verdict.reason === 'scan_the_box') {
    return `Scan ${verdict.expectedName || 'the box'} to continue.`;
  }
  if (verdict.reason === 'box_incomplete') {
    return `${verdict.expectedName || 'This box'} isn't complete yet.`;
  }
  return `Finish the hand-off first — seal ${handoff.sealBox ? handoff.sealBox.name : 'this box'}.`;
};

const refuseGuidedScan = (dispatch, getState, verdict, enforcement) => {
  audioService.playNotReadySound();
  const text = boxChampRejectionText(verdict, enforcement);
  const cardHidden = getGuidedHandoff(getState()).kind === HANDOFF_IDLE;
  if (cardHidden) clearBoxChampConfirmationTimer();
  dispatch(setScanPackBarcodeInput(''));
  dispatch(
    setBoxChampState({
      handoffDismissedKey: null,
      handoffMessage: text,
      ...(cardHidden ? {setAsideMessage: text, setAsideConfirmation: false} : {})
    })
  );
};

let refreshSeq = 0;
let mergedSeq = 0;
let mergedPlan = null;
export function refreshBoxChampBoxes() {
  return async (dispatch, getState) => {
    const before = getState().scanPackScanning.currentOrder;
    const orderId = before?.id;
    if (orderId == null) return {ok: true, detail: null};
    refreshSeq += 1;
    const seq = refreshSeq;
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const res = await axios({
        method: 'get',
        url: `${url}/orders/${orderId}.json`,
        headers: {Authorization: `Bearer ${access_token}`}
      });
      const detail = res?.data?.order;
      const latest = getState().scanPackScanning.currentOrder;
      const writtenByRefresh =
        mergedPlan != null &&
        latest?.boxes === mergedPlan.boxes &&
        latest?.order_item_boxes === mergedPlan.orderItemBoxes;
      const planWrittenSince =
        !writtenByRefresh &&
        (latest?.boxes !== before.boxes || latest?.order_item_boxes !== before.order_item_boxes);
      if (seq < mergedSeq || !detail || latest?.id !== orderId || planWrittenSince) {
        return {ok: true, detail: null};
      }
      if (boxChampOf(getState()).completionReleased) return {ok: true, detail};
      const boxes = Array.isArray(detail.boxes) ? detail.boxes : latest.boxes;
      const orderItemBoxes = Array.isArray(detail.order_item_boxes)
        ? detail.order_item_boxes
        : latest.order_item_boxes;
      mergedSeq = seq;
      mergedPlan = {boxes, orderItemBoxes};
      dispatch({
        type: SET_SCAN_PACK_CURRENT_ORDER,
        payload: {
          ...latest,
          boxes,
          order_item_boxes: orderItemBoxes,
          box_champ_warnings: detail.box_champ_warnings,
          box_champ_dirty: detail.basicinfo?.box_champ_dirty ?? latest.box_champ_dirty,
          plan_edited: detail.basicinfo?.plan_edited ?? latest.plan_edited
        }
      });
      if (Array.isArray(detail.boxes) && detail.boxes.length > 0) {
        dispatch(setBoxChampState({recalculating: false}));
      }
      return {ok: true, detail};
    } catch (error) {
      return {ok: false, error};
    }
  };
}

export function sendBoxChampPlanOp(event, boxTypeId = null, boxId = null) {
  return (dispatch, getState) => {
    const state = getState();
    const guided = boxChampOf(state);
    const view = getGuidedView(state);
    const targetBoxId = boxId ?? guided.optionsBoxId ?? (view ? view.projection.activeBoxId : null);
    if (targetBoxId == null) return;
    const {currentOrder} = getStateValues(getState);
    const log = planOpEvent(event, {
      order: {order: currentOrder},
      boxId: targetBoxId,
      boxTypeId,
      userName: userNameOf(state)
    });
    saveAndSubmitPlanOpLog(dispatch, log);
    dispatch(setBoxChampState({optionsBoxId: null, addBoxError: ''}));
  };
}

export const handleBoxChampSubstitute = (boxTypeId, boxId = null) =>
  sendBoxChampPlanOp('box_substitute', boxTypeId, boxId);
export const handleBoxChampCombine = () => sendBoxChampPlanOp('box_combine', null);
export const handleBoxChampSplit = boxTypeId => sendBoxChampPlanOp('box_split', boxTypeId);

export function confirmBoxChampHandoff() {
  return (dispatch, getState) => {
    const state = getState();
    const handoff = getGuidedHandoff(state, {ignoreModal: true});
    if (handoff.kind === HANDOFF_IDLE) return;
    const guided = boxChampOf(state);

    const {currentOrder} = getStateValues(getState);
    const order = {order: currentOrder};
    const userName = userNameOf(state);
    const firstVerify = handoff.kind === HANDOFF_AWAITING_FIRST_VERIFY;
    const event = firstVerify
      ? firstVerifyEvent({order, verifiedBoxId: handoff.expectedBox?.id, userName})
      : handoffEvent({
          order,
          sealedBoxId: handoff.sealBox?.id,
          verifiedBoxId: handoff.nextBox?.id ?? null,
          userName
        });
    const verifiedId = firstVerify ? handoff.expectedBox?.id : handoff.nextBox?.id;
    const willCompleteOrder = handoff.isLastBox && isGuidedOrderPacked(state);
    const sealing = handoff.kind === HANDOFF_PAUSED && handoff.sealBox?.id != null;

    dispatch(
      setBoxChampState({
        ...(willCompleteOrder ? {completionPending: true} : {}),
        justConfirmedBoxId: handoff.kind === HANDOFF_PAUSED ? handoff.sealBox?.id ?? null : null,
        sealConfirmedBoxIds: sealing
          ? [...guided.sealConfirmedBoxIds, handoff.sealBox.id]
          : guided.sealConfirmedBoxIds,
        handoffMessage: '',
        handoffVerifiedBoxIds:
          verifiedId == null
            ? guided.handoffVerifiedBoxIds
            : [...guided.handoffVerifiedBoxIds, verifiedId]
      })
    );

    if (!willCompleteOrder) {
      playGuidedSuccess();
      dispatch(showBoxChampConfirmation(boxChampConfirmMessage(handoff)));
    }

    saveAndSubmitPlanOpLog(dispatch, event);

    const after = getState();
    if (!v2FocusBlockingModalOpen(after) && !guidedHandoffOwnsFocus(after)) {
      dispatch(setScanPackMainInputFocus(true));
    }
  };
}

export function openBoxChampOptions() {
  return (dispatch, getState) => {
    const state = getState();
    const view = getGuidedView(state);
    const boxId = view ? view.projection.activeBoxId : null;
    if (boxId == null) return;
    if (!boxChampOf(state).catalog?.length) dispatch(loadBoxChampCatalog());
    dispatch(setBoxChampState({optionsBoxId: boxId, addBoxError: ''}));
  };
}

export const closeBoxChampOptions = () => setBoxChampState({optionsBoxId: null, addBoxError: ''});

const addBoxErrorFrom = errors =>
  (Array.isArray(errors) && errors.join(', ')) || 'Could not save that box.';

export function handleBoxChampAddBox(payload) {
  return async (dispatch, getState) => {
    const orderId = getState().scanPackScanning.currentOrder?.id;
    const visit = catalogVisit;
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const res = await axios({
        method: 'post',
        url: `${url}/box_types/packing_create.json`,
        headers: {Authorization: `Bearer ${access_token}`},
        data: payload
      });
      if (visit !== catalogVisit) return;
      if (res?.data?.status && res.data.box_type) {
        const {catalog = []} = boxChampOf(getState());
        dispatch(setBoxChampState({catalog: [...catalog, res.data.box_type], addBoxError: ''}));
        if (getState().scanPackScanning.currentOrder?.id !== orderId) return;
        dispatch(handleBoxChampSubstitute(res.data.box_type.id));
      } else {
        dispatch(setBoxChampState({addBoxError: addBoxErrorFrom(res?.data?.errors)}));
      }
    } catch (e) {
      if (visit !== catalogVisit) return;
      dispatch(setBoxChampState({addBoxError: addBoxErrorFrom(e?.response?.data?.errors)}));
    }
  };
}

let planInFlightOrderId = null;

export const resetBoxChampPlanRequest = () => {
  planInFlightOrderId = null;
};

export function calculateBoxChampPlan() {
  return (dispatch, getState) => {
    const orderId = getState().scanPackScanning.currentOrder?.id;
    if (orderId == null || planInFlightOrderId === orderId) return undefined;
    planInFlightOrderId = orderId;
    dispatch(setBoxChampState({recalculating: true, planError: ''}));
    return Promise.resolve(
      dispatch(
        CalculateBoxChampBoxes(orderId, (success, data) => {
          if (planInFlightOrderId === orderId) planInFlightOrderId = null;
          if (getState().scanPackScanning.currentOrder?.id !== orderId) return;
          if (success) {
            dispatch(refreshBoxChampBoxes());
          } else {
            dispatch(
              setBoxChampState({
                recalculating: false,
                planError: data?.error || 'Could not recalculate the box plan.'
              })
            );
          }
        })
      )
    ).catch(() => {
      if (planInFlightOrderId === orderId) planInFlightOrderId = null;
      if (getState().scanPackScanning.currentOrder?.id !== orderId) return;
      dispatch(setBoxChampState({recalculating: false, planError: 'Could not recalculate the box plan.'}));
    });
  };
}

export const retryBoxChampPlan = calculateBoxChampPlan;

export function handleBoxChampDimsSaveAll(payloads) {
  return async (dispatch, getState) => {
    const order = getState().scanPackScanning.currentOrder;
    const ids = payloads.map(p => p.product_id);
    await Promise.all(
      payloads.map(p => dispatch(UpdateProductInfo(p.product_id, p.data, order?.increment_id)))
    );
    const latest = getState().scanPackScanning.currentOrder;
    if (!latest || latest.id !== order?.id) return;
    const clear = list =>
      (list || []).map(item => ({
        ...item,
        needs_dims: ids.includes(item.product_id) ? false : item.needs_dims,
        child_items: item.child_items ? clear(item.child_items) : item.child_items
      }));
    dispatch({
      type: SET_SCAN_PACK_CURRENT_ORDER,
      payload: {...latest, unscanned_items: clear(latest.unscanned_items)}
    });
    dispatch(setCurrentItem());
    dispatch(calculateBoxChampPlan());
  };
}

export function dismissBoxChampHandoff() {
  return (dispatch, getState) => {
    const key = guidedHandoffKey(getGuidedHandoff(getState()));
    if (key !== null) dispatch(setBoxChampState({handoffDismissedKey: key}));
  };
}

export const showBoxChampHandoff = () => setBoxChampState({handoffDismissedKey: null});

const guidedBoxScan = (dispatch, getState, rawValue) => {
  const state = getState();
  const view = getGuidedView(state);
  if (!view) return false;
  const guided = boxChampOf(state);
  const activeId = view.projection.activeBoxId;
  const entry = view.projection.boxes.find(e => e.box.id === activeId);
  const step = substitutionStep(rawValue, {
    expectedBox: entry ? entry.box : null,
    catalog: guided.catalog,
    pending: guided.pendingSubstitution,
    settings: scanPackSettingsOf(state)
  });
  if (!step.handled) return false;

  dispatch(setScanPackBarcodeInput(''));
  if (step.commitBoxTypeId != null) {
    dispatch(setBoxChampState({pendingSubstitution: null}));
    dispatch(handleBoxChampSubstitute(step.commitBoxTypeId, activeId));
    return true;
  }
  if (step.prompt) {
    clearBoxChampConfirmationTimer();
    dispatch(
      setBoxChampState({
        pendingSubstitution: step.state === SUBSTITUTION_PENDING ? step.pending : null,
        setAsideMessage: step.prompt,
        setAsideConfirmation: false
      })
    );
    return true;
  }
  playGuidedSuccess();
  const name = entry?.box?.name || 'This box';
  dispatch(
    showBoxChampConfirmation(
      step.cancelled ? `${name} confirmed — substitution cancelled.` : `${name} confirmed.`,
      {pendingSubstitution: null}
    )
  );
  return true;
};

const rowsForItem = (entry, orderItemId, kitId) =>
  entry.rows.filter(
    r =>
      r.row.order_item_id === orderItemId &&
      (kitId == null ? r.row.kit_id == null : r.row.kit_id === kitId)
  );

const earlySetAsideFor = (view, item, kitId) => {
  const activeId = view.projection.activeBoxId;
  if (activeId == null || !item || item.order_item_id == null) return null;
  const active = view.projection.boxes.find(e => e.box.id === activeId);
  if (!active || rowsForItem(active, item.order_item_id, kitId).some(r => r.filled < r.target)) {
    return null;
  }
  const later = view.projection.boxes.find(
    e =>
      e.box.id !== activeId &&
      !e.sealed &&
      rowsForItem(e, item.order_item_id, kitId).some(r => r.filled < r.target)
  );
  if (!later) return null;
  const [{row}] = rowsForItem(later, item.order_item_id, kitId);
  const label = guidedLabelFor(view, row);
  const child =
    kitId == null ? null : (item.child_items || []).find(c => c && c.kit_product_id === kitId);
  const name = (label && (label.name || label.sku)) || child?.name || item.name || item.sku || 'This item';
  return `Set that aside — ${name} goes in ${later.box.name}`;
};

const scannedItemFor = (unscanned, value, caseInsensitive) => {
  const norm = v => (caseInsensitive ? String(v).toLowerCase() : String(v));
  const target = norm(value);
  if (!target) return null;
  const matches = it => (it?.barcodes || []).some(b => b && norm(b.barcode) === target);
  let found = null;
  unscanned.some(it => {
    if (!it || it.order_item_id == null) return false;
    const children = Array.isArray(it.child_items) ? it.child_items : [];
    if (!children.length) {
      if (matches(it)) found = {item: it, kitId: null};
      return Boolean(found);
    }
    const child = children.find(c => c && c.kit_product_id != null && matches(c));
    if (child) found = {item: it, kitId: child.kit_product_id};
    return Boolean(found);
  });
  return found;
};

const badgeBoxScan = (dispatch, getState, rawValue) => {
  const state = getState();
  const boxes = state.scanPackScanning.currentOrder?.boxes;
  const match = matchScannedBox(rawValue, boxes);
  if (!match.isBoxScan) return false;
  const box = boxes[match.matchedIndex];
  const guided = boxChampOf(state);
  dispatch(setScanPackBarcodeInput(''));
  dispatch(setActiveBoxIndex(match.matchedIndex));
  dispatch(
    setBoxChampState({
      badgeVerifiedBoxIds: Array.from(new Set([...(guided.badgeVerifiedBoxIds || []), box?.id]))
    })
  );
  playGuidedSuccess();
  dispatch(showBoxChampConfirmation(`${box?.name || 'Box'} verified.`));
  return true;
};

export function runGuidedScanPrelude({rawValue, processedValue, caseInsensitive}) {
  return (dispatch, getState) => {
    const state = getState();
    const raw = String(rawValue ?? '').trim();
    const view = getGuidedView(state);
    const enforcement = getGuidedHandoff(state, {ignoreModal: true});

    if (view) {
      const verdict = classifyPausedInput(raw, {
        state: enforcement,
        catalog: boxChampOf(state).catalog,
        unboxedBarcodes: getGuidedUnboxedBarcodes(view),
        settings: scanPackSettingsOf(state)
      });
      if (verdict.action === 'confirm') {
        dispatch(setScanPackBarcodeInput(''));
        dispatch(confirmBoxChampHandoff());
        return true;
      }
      if (verdict.action === 'reject') {
        refuseGuidedScan(dispatch, getState, verdict, enforcement);
        return true;
      }
      if (enforcement.kind === HANDOFF_IDLE && guidedBoxScan(dispatch, getState, raw)) {
        return true;
      }
      if (enforcement.kind === HANDOFF_IDLE) {
        const unscanned = state.scanPackScanning.currentOrder?.unscanned_items || [];
        const hit = scannedItemFor(unscanned, processedValue, caseInsensitive);
        const message = hit && earlySetAsideFor(view, hit.item, hit.kitId);
        if (message) {
          setItemAside(dispatch, message);
          return true;
        }
      }
      const upper = raw.toUpperCase();
      if ((upper === 'BOXPREV' || upper === 'BOXNEXT') && view) {
        setItemAside(dispatch, BOX_CHAMP_MANUAL_NAV_MESSAGE);
        return true;
      }
      if (boxChampOf(getState()).pendingSubstitution) {
        clearBoxChampConfirmationTimer();
        dispatch(
          setBoxChampState({pendingSubstitution: null, setAsideMessage: '', setAsideConfirmation: false})
        );
      }
    }

    return badgeBoxScan(dispatch, getState, raw);
  };
}

export function guidedRefusesItemScan(item) {
  return (dispatch, getState) => {
    const state = getState();
    const view = getGuidedView(state);
    if (!view || !item) return false;
    const enforcement = getGuidedHandoff(state, {ignoreModal: true});
    if (enforcement.kind !== HANDOFF_IDLE) {
      if (view.unboxedItems.some(unboxed => unboxed.order_item_id === item.order_item_id)) return false;
      const firstBarcode = (item.barcodes || []).map(b => b && b.barcode).find(Boolean) || '';
      const verdict = classifyPausedInput(firstBarcode, {
        state: enforcement,
        catalog: boxChampOf(state).catalog,
        unboxedBarcodes: getGuidedUnboxedBarcodes(view),
        settings: scanPackSettingsOf(state)
      });
      refuseGuidedScan(dispatch, getState, verdict.action === 'reject' ? verdict : {action: 'reject'}, enforcement);
      return true;
    }
    const kitId = item.kit_product_id ?? null;
    const message = earlySetAsideFor(view, item, kitId);
    if (message) {
      setItemAside(dispatch, message);
      return true;
    }
    return false;
  };
}


export function guidedRefusesScanAll() {
  return (dispatch, getState) => {
    const state = getState();
    if (!getGuidedView(state) || isGuidedOrderPacked(state)) return false;
    audioService.playNotReadySound();
    dispatch(setScanPackBarcodeInput(''));
    showSetAsideMessage(dispatch, BOX_CHAMP_SCAN_ALL_MESSAGE);
    return true;
  };
}

export function recordGuidedNotFulfilled(removedItem) {
  return (dispatch, getState) => {
    const state = getState();
    if (!getGuidedView(state) || !removedItem) return;
    const order = state.scanPackScanning.currentOrder;
    const local = boxChampOf(state).notFulfilledItems;
    const itemToProcess = getFirstItem([removedItem]);
    dispatch(
      setBoxChampState({
        notFulfilledItems: [
          ...(local ?? order?.not_fulfilled_items ?? []),
          {...itemToProcess, skipped_qty: itemToProcess.qty_remaining, qty_remaining: 0}
        ]
      })
    );
  };
}
